import { ComponentItem } from "designSystem/types/component-item-types";
import { ILocaleProvider } from "designSystem/locale";
import _ from "lodash";
import { action, makeObservable, observable } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";
import { C8NodeType } from "src/c8-platform/type/node";
import { APGroup, calculateAttributeGroups, getAttributePickerComponentItems } from "./attributePickerService";

export default class AttributePickerStore {
	private _expandSet = observable.set<string>();
	@observable.ref private _groups: APGroup[] = [];

	readonly depthOptions: ComponentItem<number>[];
	readonly domainClassOptions: ComponentItem[];
	readonly loc: ILocaleProvider;

	@observable.ref depthOption: ComponentItem<number>;
	@observable.ref domainClassOption: ComponentItem;
	@observable expand = false;
	@observable searchText = "";

	get depth(): number {
		return this.depthOption.value;
	}

	get domainType(): C8NodeType | undefined {
		return this.domainClassOption?.id;
	}

	get groups(): APGroup[] {
		// TODO: should we filter groups by text, or just attributes within group?
		const { depth, searchTextLower } = this;
		return this._groups.filter((group) => (group.depth <= depth) && group.matches(searchTextLower));
	}

	get searchTextLower(): string {
		return this.searchText.toLowerCase();
	}

	getGroupAttributes = (group: APGroup): ComponentItem[] => {
		const { searchTextLower } = this;
		return group.attributes.filter((a) => a.matches(searchTextLower));
	};

	isGroupExpanded = ({ id }: ComponentItem): boolean => {
		return this._expandSet.has(id);
	};

	@action onChangeDepth = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		const { depth, _recalculate } = this;

		this.depthOption = value;

		// if depth increased, the calculate new groups and attributes;
		// existing groups at greater depth will be filtered out when rendering
		if (this.depth > depth) {
			_recalculate();
		}
	};

	@action onChangeDomainClass = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.domainClassOption = value;
		this._recalculate();
	};

	@action onChangeSearch = (event: ChangeEvent<HTMLInputElement>): void => {
		this.searchText = event.target.value;
	};

	@action onExpand = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		const { _expandSet, _groups } = this;

		this.expand = value;

		if (value) {
			for (const { id } of _groups) {
				_expandSet.add(id);
			}
		} else {
			_expandSet.clear();
		}
	};

	@action toggleGroupCollapsed = ({ id }: ComponentItem): void => {
		const { _expandSet } = this;

		if (_expandSet.has(id)) {
			_expandSet.delete(id);
		} else {
			_expandSet.add(id);
		}
	};

	private _recalculate = (): void => {
		const { depth, domainType, loc, _expandSet } = this;
		this._groups = domainType ? calculateAttributeGroups(domainType, depth, loc) : [];
		_expandSet.clear();
	};

	constructor(loc: ILocaleProvider) {
		makeObservable(this);
		this.depthOptions = _.range(1, 7).map((n) => ({
			id: n.toString(),
			value: n,
		}));
		this.depthOption = this.depthOptions[1];
		this.domainClassOptions = getAttributePickerComponentItems(loc);
		this.loc = loc;
	}
};

export const AttributePickerStoreContext = createContext<AttributePickerStore>(undefined);
export const useAttributePickerStore = () => useContext(AttributePickerStoreContext);